import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { RestaurantCard } from "../components/RestaurantCard";
import { RestaurantCarousel } from "../components/RestaurantCarousel";
import { RestaurantMap } from "../components/RestaurantMap";
import { MemberBanner } from "../components/MemberBanner";
import { PartnerBanner } from "../components/PartnerBanner";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { Search, Navigation, Wallet, ArrowRight, Map as MapIcon } from "lucide-react";
import { Seo } from "../components/Seo";

const MARQUEE = ["ITALIEN", "SUSHI", "BURGERS", "VEGAN", "HEALTHY", "INDIEN", "FRANÇAIS"];

export default function Marketplace() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [search, setSearch] = useState("");
  const [cuisine, setCuisine] = useState("all");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [showMap, setShowMap] = useState(false);

  // Thematic sections (only shown when user hasn't applied a filter yet)
  const [sections, setSections] = useState({ popular: [], newest: [], fast: [], featured: [] });
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const filtersActive =
    Boolean(search) || cuisine !== "all" || sort !== "featured" || Boolean(coords);
  const showSections = !filtersActive && sectionsLoaded;

  const handleNearMe = () => {
    if (!navigator.geolocation) {
      toast.error(t("toast.geoUnsupported"));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSort("distance");
        setShowMap(true);
        setLocating(false);
        toast.success(t("toast.nearMeSuccess"));
      },
      () => {
        setLocating(false);
        toast.error(t("toast.geoDenied"));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    api.get("/cuisines").then((r) => setCuisines(r.data));
  }, []);

  // Load thematic carousels once at mount
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [popular, newest, fast, featured] = await Promise.all([
          api.get("/restaurants", { params: { sort: "rating", limit: 10 } }),
          api.get("/restaurants", { params: { sort: "new", limit: 10 } }),
          api.get("/restaurants", { params: { sort: "fast", limit: 10 } }),
          api.get("/restaurants", { params: { sort: "featured", limit: 10 } }),
        ]);
        if (!active) return;
        setSections({
          popular: popular.data,
          newest: newest.data,
          fast: fast.data,
          featured: featured.data,
        });
        setSectionsLoaded(true);
      } catch (e) {
        if (active) setSectionsLoaded(true); // graceful — hide if fails
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      try {
        const params = { search: search, cuisine, sort };
        if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
        const res = await api.get("/restaurants", { params });
        if (active) setRestaurants(res.data);
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [search, cuisine, sort, coords]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        path="/"
        title="Vytali — Commander en ligne au Québec | Marketplace de restaurants partenaires"
        description="Commande en ligne + livraison partout au Québec avec 5% de cashback membre. Découvrez nos restaurants, épiceries et traiteurs partenaires. L'alternative québécoise à UberEats & DoorDash."
        keywords="commander en ligne Québec, restaurants livraison Québec, alternative UberEats, alternative DoorDash, Vytali, épicerie livraison Québec, restaurant Montréal Trois-Rivières"
        image="https://images.unsplash.com/photo-1673166516558-3f1b88a22db8?crop=entropy&cs=srgb&fm=jpg&q=90&w=1200&h=630&fit=crop"
      />
      <Header />

      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8">
        <MemberBanner />
        <PartnerBanner />
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5C5C5C] font-mono">
              {t("hero.overline")}
            </p>
            <h1 className="mt-4 text-5xl md:text-6xl font-display font-black tracking-tighter leading-[0.95] text-[#2B2B2B]">
              {t("hero.title1")}<span className="text-[#E63946]">{t("hero.highlight")}</span>{t("hero.title2")}
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#5C5C5C] max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 max-w-xl">
              <div
                data-testid="search-bar"
                className="flex-1 min-w-[240px] h-16 px-5 bg-white border-2 border-[#E5E0D8] rounded-full flex items-center gap-3 focus-within:border-[#E63946] focus-within:shadow-[4px_4px_0px_#2B2B2B] transition-[border-color,box-shadow]"
              >
                <Search className="w-5 h-5 text-[#5C5C5C]" />
                <input
                  data-testid="search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("search.placeholder")}
                  className="flex-1 bg-transparent outline-none text-[#2B2B2B] placeholder:text-[#9C9C9C]"
                />
              </div>
              <button
                data-testid="hero-near-me-btn"
                onClick={handleNearMe}
                disabled={locating}
                aria-label={t("filters.nearMe")}
                className={`h-16 px-6 rounded-full flex items-center gap-2 font-bold text-sm transition-[transform,background-color,color] disabled:opacity-60 ${
                  coords
                    ? "bg-[#E63946] text-white hover:scale-[1.03]"
                    : "bg-[#2B2B2B] text-white hover:bg-[#E63946] hover:scale-[1.03]"
                }`}
              >
                <Navigation className="w-4 h-4" />
                <span className="whitespace-nowrap">
                  {locating ? t("filters.locating") : coords ? t("filters.aroundMe") : t("filters.nearMe")}
                </span>
              </button>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-[#E5E0D8] aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1673166516558-3f1b88a22db8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Burger gourmet"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-[#E5E0D8] bg-[#F4EFE6] py-3 overflow-hidden">
        <div className="marquee-track">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="mx-6 text-lg font-display font-bold text-[#2B2B2B]/70">
              {m} <span className="text-[#E63946]">—</span>
            </span>
          ))}
        </div>
      </div>

      {/* Filters + Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Membership banner */}
        <div
          data-testid="membership-banner"
          onClick={() => navigate("/membre")}
          className="mb-10 cursor-pointer rounded-2xl bg-[#2B2B2B] text-[#FDFBF7] p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 hover:shadow-[8px_8px_0px_#E63946] transition-shadow"
        >
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 rounded-full bg-[#E63946] flex items-center justify-center shrink-0">
              <Wallet className="w-6 h-6 text-white" />
            </span>
            <div>
              <h3 className="text-xl md:text-2xl font-display font-bold tracking-tight">{t("mb.bannerTitle")}</h3>
              <p className="text-sm text-[#FDFBF7]/80">{t("mb.bannerText")}</p>
            </div>
          </div>
          <span className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#E63946] text-white font-bold text-sm shrink-0">
            {t("mb.bannerCta")} <ArrowRight className="w-4 h-4" />
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            data-testid="cuisine-filter-all"
            onClick={() => setCuisine("all")}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${cuisine === "all" ? "bg-[#2B2B2B] text-white" : "bg-white border border-[#E5E0D8] text-[#2B2B2B] hover:bg-[#F4EFE6]"}`}
          >
            {t("filters.all")}
          </button>
          {cuisines.map((c) => (
            <button
              key={c}
              data-testid={`cuisine-filter-${c}`}
              onClick={() => setCuisine(c)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${cuisine === c ? "bg-[#2B2B2B] text-white" : "bg-white border border-[#E5E0D8] text-[#2B2B2B] hover:bg-[#F4EFE6]"}`}
            >
              {c}
            </button>
          ))}
          <button
            data-testid="near-me-btn"
            onClick={handleNearMe}
            disabled={locating}
            className={`ml-auto flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors disabled:opacity-60 ${coords ? "bg-[#E63946] text-white" : "bg-white border border-[#E5E0D8] text-[#2B2B2B] hover:bg-[#F4EFE6]"}`}
          >
            <Navigation className="w-4 h-4" /> {locating ? t("filters.locating") : coords ? t("filters.aroundMe") : t("filters.nearMe")}
          </button>
          <button
            data-testid="toggle-map-btn"
            onClick={() => setShowMap((v) => !v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${showMap ? "bg-[#2B2B2B] text-white" : "bg-white border border-[#E5E0D8] text-[#2B2B2B] hover:bg-[#F4EFE6]"}`}
          >
            <MapIcon className="w-4 h-4" /> {showMap ? t("map.hide") : t("map.show")}
          </button>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-4 py-2 rounded-full text-sm font-bold bg-white border border-[#E5E0D8] text-[#2B2B2B] outline-none cursor-pointer"
          >
            <option value="featured">{t("sort.featured")}</option>
            <option value="rating">{t("sort.rating")}</option>
            {coords && <option value="distance">{t("sort.nearest")}</option>}
          </select>
        </div>

        {showMap && (
          <div className="mb-8">
            <RestaurantMap restaurants={restaurants} coords={coords} />
          </div>
        )}

        {/* Thematic sections (Netflix / UberEats-style horizontal carousels).
            Hidden as soon as the user applies any filter/search/sort/near-me. */}
        {showSections && (
          <div className="space-y-12 mb-16">
            {sections.popular.length > 0 && (
              <RestaurantCarousel
                testIdPrefix="popular"
                emoji="🔥"
                title={t("sections.popular.title")}
                subtitle={t("sections.popular.subtitle")}
                restaurants={sections.popular}
              />
            )}
            {sections.fast.length > 0 && (
              <RestaurantCarousel
                testIdPrefix="fast"
                emoji="⚡"
                title={t("sections.fast.title")}
                subtitle={t("sections.fast.subtitle")}
                restaurants={sections.fast}
                accent="#F4A261"
              />
            )}
            {sections.newest.length > 0 && (
              <RestaurantCarousel
                testIdPrefix="newest"
                emoji="🆕"
                title={t("sections.new.title")}
                subtitle={t("sections.new.subtitle")}
                restaurants={sections.newest}
                accent="#2A9D8F"
              />
            )}
            {sections.featured.length > 0 && (
              <RestaurantCarousel
                testIdPrefix="featured"
                emoji="❤️"
                title={t("sections.featured.title")}
                subtitle={t("sections.featured.subtitle")}
                restaurants={sections.featured}
              />
            )}
          </div>
        )}

        {showSections && (
          <div className="mb-6">
            <h2 className="text-2xl md:text-3xl font-display font-black tracking-tight text-[#2B2B2B]">
              {t("sections.all.title")}
            </h2>
            <p className="mt-1 text-sm text-[#5C5C5C] font-mono">
              {t("sections.all.subtitle")}
            </p>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-80 rounded-xl bg-[#F4EFE6] animate-pulse" />
            ))}
          </div>
        ) : restaurants.length === 0 ? (
          <p data-testid="no-results" className="text-center text-[#5C5C5C] py-20 font-mono">
            {t("grid.noResults")}
          </p>
        ) : (
          <div data-testid="restaurant-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {restaurants.map((r, i) => (
              <RestaurantCard key={r.id} restaurant={r} index={i} />
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-[#E5E0D8] py-10 mt-10" style={{ display: "none" }} />
      <Footer />
    </div>
  );
}
